import React, { useEffect, useState } from "react";
import styles from "./Home.module.css";
import { reactIcons } from "../../icons/reactIcons";
import noImage from "../../assets/images/no_image.png";
import ReviewImageModal from "./modal/ReviewImageModal";
import { reverseGeocode } from "../../utils/reverseGeocode";

interface HomeReportsProps {
  reports: any[];
}

const HomeReports: React.FC<HomeReportsProps> = ({ reports }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [activeImages, setActiveImages] = useState<string[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [addresses, setAddresses] = useState<Record<string, string>>({});
  const [imageLoading, setImageLoading] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!reports || reports.length === 0) return;

    reports.forEach(async (report) => {
      if (report.geoTag?.lat && report.geoTag?.lon) {
        const addr = await reverseGeocode(report.geoTag.lat, report.geoTag.lon);

        setAddresses((prev) => ({
          ...prev,
          [report.id]: addr,
        }));
      }
    });
  }, [reports]);

  const handleImageClick = (images: string[], index: number) => {
    setActiveImages(images);
    setActiveIndex(index);
    setModalOpen(true);
  };

  const openInMaps = (lat: number, lon: number) => {
    const scheme = /iPhone|iPad|Macintosh/i.test(navigator.userAgent)
      ? `maps://maps.apple.com/?q=${lat},${lon}`
      : `https://www.google.com/maps?q=${lat},${lon}`;

    window.open(scheme, "_blank");
  };

  if (!reports.length) {
    return (
      <div className={styles.noReports}>
        <div className={styles.noReportsCard}>
          <reactIcons.ReportIcon className={styles.noReportsIcon} />
          <h3>No Reports Yet</h3>
          <p>Your submitted reports will appear here once available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.reportList}>
      {reports.map((report) => {
        const formattedDate = new Date(report.createdAt).toLocaleString();
        const images = report.files?.map((f: any) => f.signedUrl) || [];

        return (
          <div key={report.id} className={styles.reportCard}>
            <div className={styles.reportRow}>
              <reactIcons.CalenderIcon className={styles.icon} />
              <div>
                <strong>Date Submitted</strong>
                <p>{formattedDate}</p>
              </div>
            </div>

            <div className={styles.reportRow}>
              <reactIcons.MapPinIcon className={styles.icon} />
              <div>
                <strong>Location</strong>
                <p>
                  {addresses[report.id]
                    ? addresses[report.id]
                    : "Fetching address..."}
                </p>
                <p className={styles.lat_lng}>
                  {report.geoTag?.lat}° N, {report.geoTag?.lon}° W
                </p>

                <button
                  className={styles.visitButton}
                  onClick={() =>
                    openInMaps(
                      Number(report.geoTag?.lat),
                      Number(report.geoTag?.lon)
                    )
                  }
                >
                  <reactIcons.FaMapMarkerIcon color="#0155ae" /> Visit Location
                </button>
              </div>
            </div>

            <div className={styles.reportRow}>
              <div>
                <reactIcons.NoteIcon className={styles.icon} />
              </div>
              <div>
                <strong>Note</strong>
                <p>{report.notes}</p>
              </div>
            </div>

            <div className={styles.imageSection}>
              <strong>Images</strong>
              <div className={styles.imageGrid}>
                {images.length > 0 ? (
                  images.map((img: any, i: number) => {
                    const key = `${report.id}-${i}`;

                    return (
                      <div className={styles.imageWrapper} key={key}>
                        {imageLoading[key] !== false && (
                          <div className={styles.imageSpinner}></div>
                        )}

                        <img
                          src={img}
                          alt={`report-${i}`}
                          className={`${styles.reportImage} ${
                            imageLoading[key] !== false
                              ? styles.hiddenImage
                              : ""
                          }`}
                          onLoad={() =>
                            setImageLoading((prev) => ({
                              ...prev,
                              [key]: false,
                            }))
                          }
                          onError={() =>
                            setImageLoading((prev) => ({
                              ...prev,
                              [key]: false,
                            }))
                          }
                          onClick={() => handleImageClick(images, i)}
                        />
                      </div>
                    );
                  })
                ) : (
                  <img src={noImage} className={styles.reportImage} />
                )}
              </div>
            </div>
          </div>
        );
      })}

      <ReviewImageModal
        open={modalOpen}
        images={activeImages}
        currentIndex={activeIndex}
        onClose={() => setModalOpen(false)}
        onNext={() => setActiveIndex((prev) => prev + 1)}
        onPrev={() => setActiveIndex((prev) => prev - 1)}
      />
    </div>
  );
};

export default HomeReports;
